/** Read-only reconciliation of already authorised, client-scoped source projections.
 * This module does not establish access, source completeness or clinical outcomes.
 */
export const DAILY_CARE_SOURCES = [
  'tasks',
  'roster',
  'services',
  'rounds',
  'adl',
  'medicationSchedule',
  'mar',
  'woundTasks',
] as const;
export type DailyCareSource = (typeof DAILY_CARE_SOURCES)[number];
export const DAILY_CARE_SOURCE_LABELS: Record<DailyCareSource, string> = {
  tasks: 'Tasks',
  roster: 'Roster visits',
  services: 'Delivered services',
  rounds: 'Care rounds',
  adl: 'Daily living records',
  medicationSchedule: 'Prescribed medication',
  mar: 'Medication administration',
  woundTasks: 'Wound follow-up',
};
export type DailyCareCoverage =
  | 'ready'
  | 'unavailable'
  | 'limited'
  | 'not-applicable'
  | 'not-permitted';
export type DailyCareOutcome =
  | 'scheduled'
  | 'overdue'
  | 'in_progress'
  | 'recorded'
  | 'delivered'
  | 'refused'
  | 'omitted'
  | 'missed'
  | 'withheld'
  | 'not_available'
  | 'self_administered'
  | 'unknown';
export type DailyCarePriority = 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
export const DAILY_CARE_ORDER_VERSION = 'recorded-priority-and-status-v1';
export const DAILY_CARE_ORDER_DESCRIPTION =
  'Recorded urgent and high-priority tasks first within the available items, then exceptions and unresolved care, ordered by recorded time. Undated items follow dated items within each group. Recorded activity remains visible and does not confirm all care is complete.';

export type DailyCareSourceRow = {
  id: string;
  clientId: string;
  contextRevision: string;
  label: string;
  /** Source-owner route, supplied by the authorised reader. */
  href: string;
  at: string | null;
  outcome: DailyCareOutcome;
  /** Recorded source priority only; never inferred from clinical text. */
  priority?: DailyCarePriority | null;
  /** Explicit source foreign key; never inferred from time or description. */
  rosterShiftId?: string | null;
  roundAssignmentId?: string | null;
  medicationOccurrenceId?: string | null;
  /** Programmes whose controlled readers admitted this row. */
  programFamilies?: string[];
};
export type DailyCareSourceProjection = {
  coverage: DailyCareCoverage;
  rows: DailyCareSourceRow[];
};
export type DailyCareInput = {
  clientId: string;
  contextRevision: string;
  sources: Record<DailyCareSource, DailyCareSourceProjection>;
};
export type DailyCareItem = {
  id: string;
  source: DailyCareSource;
  row: DailyCareSourceRow;
  evidence: Array<{ source: DailyCareSource; row: DailyCareSourceRow }>;
};
export type DailyCareProjection = {
  coverage: Record<DailyCareSource, DailyCareCoverage>;
  items: DailyCareItem[];
  /** True only describes coverage, never completion of care. */
  declaredSourcesCovered: boolean;
};

export function projectDailyCare(input: DailyCareInput): DailyCareProjection {
  if (!input.clientId || !input.contextRevision) throw new Error('daily_care_scope_required');
  const coverage = {} as Record<DailyCareSource, DailyCareCoverage>;
  const items: DailyCareItem[] = [];
  const anchors = new Map<string, DailyCareItem>();
  for (const source of DAILY_CARE_SOURCES) {
    const projection = input.sources[source];
    if (!projection) throw new Error('daily_care_source_required');
    if (
      !['ready', 'unavailable', 'limited', 'not-applicable', 'not-permitted'].includes(
        projection.coverage,
      )
    )
      throw new Error('daily_care_invalid_coverage');
    coverage[source] = projection.coverage;
    const seen = new Set<string>();
    for (const row of projection.rows) {
      if (row.clientId !== input.clientId || row.contextRevision !== input.contextRevision)
        throw new Error('daily_care_scope_mismatch');
      if (!row.id || seen.has(row.id)) throw new Error('daily_care_duplicate_source_row');
      seen.add(row.id);
      if (
        projection.coverage === 'not-permitted' ||
        projection.coverage === 'not-applicable' ||
        projection.coverage === 'unavailable'
      )
        throw new Error('daily_care_invalid_source_rows');
      const target =
        source === 'services' && row.rosterShiftId
          ? anchors.get(`roster:${row.rosterShiftId}`)
          : source === 'adl' && row.roundAssignmentId
            ? anchors.get(`rounds:${row.roundAssignmentId}`)
            : source === 'mar' && row.medicationOccurrenceId
              ? anchors.get(`medicationSchedule:${row.medicationOccurrenceId}`)
              : undefined;
      const commonProgramme =
        !target ||
        !target.row.programFamilies ||
        !row.programFamilies ||
        target.row.programFamilies.some((family) => row.programFamilies!.includes(family));
      if (target && commonProgramme) {
        target.evidence.push({ source, row });
      } else {
        const item = { id: `${source}:${row.id}`, source, row, evidence: [] } as DailyCareItem;
        items.push(item);
        if (source === 'roster' || source === 'rounds' || source === 'medicationSchedule')
          anchors.set(item.id, item);
      }
    }
  }
  return {
    coverage,
    items: orderDailyCareItems(items),
    declaredSourcesCovered: DAILY_CARE_SOURCES.every(
      (source) => coverage[source] === 'ready' || coverage[source] === 'not-applicable',
    ),
  };
}

/** Presentation order only: no outcome, instruction or escalation is changed. */
export function orderDailyCareItems(items: DailyCareItem[]): DailyCareItem[] {
  const exceptions = new Set<DailyCareOutcome>([
    'refused',
    'omitted',
    'missed',
    'withheld',
    'not_available',
    'unknown',
  ]);
  const recorded = new Set<DailyCareOutcome>(['recorded', 'delivered', 'self_administered']);
  const rank = (item: DailyCareItem) => {
    if (item.row.priority === 'URGENT') return 0;
    if (item.row.priority === 'HIGH') return 1;
    const facts = [item.row, ...item.evidence.map((entry) => entry.row)];
    if (facts.some((fact) => exceptions.has(fact.outcome))) return 2;
    return facts.some((fact) => recorded.has(fact.outcome)) ? 4 : 3;
  };
  const time = (item: DailyCareItem) => {
    const value = item.row.at === null ? NaN : Date.parse(item.row.at);
    return Number.isFinite(value) ? value : Infinity;
  };
  return [...items].sort((a, b) => {
    const priorityDifference = rank(a) - rank(b);
    if (priorityDifference) return priorityDifference;
    const aTime = time(a),
      bTime = time(b);
    if (aTime !== bTime) return aTime < bTime ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}
